import { Guid } from '@approvalmax/types';
import { Button, Checkbox } from '@approvalmax/ui';
import { Divider, Popup } from '@approvalmax/ui/src/components';
import { useAtom } from 'jotai';
import { memo, useCallback, useState } from 'react';
import bemFactory from 'react-bem-factory';
import { useChangeWatchers } from 'shared/data/webApp/v1';

import { messages } from './AddAsWatchersPopup.messages';
import { addAsWatcherPopupState } from './AddAsWatchersPopup.states';
import { Content, Description, ListHeader, ListStyled, PaddedGridHeader } from './AddAsWatchersPopup.styles';
import { AddAsWatchersPopupProps } from './AddAsWatchersPopup.types';
import UserList from './components/UserList/UserList';

const qa = bemFactory.qa('reql-add-as-watchers-popup');

export const AddAsWatchersPopup = memo<AddAsWatchersPopupProps>((props) => {
    const { request } = props;

    const [watchersPopup, setWatchersPopup] = useAtom(addAsWatcherPopupState);
    const [checkedUserIds, setCheckedUserIds] = useState<Guid[]>([]);

    const { usersWithoutAccess: users, onSubmit, onClose } = watchersPopup;
    const { mutateAsync: changeWatchers, isLoading: isLoadingChangeWatchers } = useChangeWatchers();

    const selectAllUsers = useCallback(() => {
        setCheckedUserIds((currentCheckedUserIds) =>
            currentCheckedUserIds.length === users.length ? [] : users.map((user) => user.id)
        );
    }, [users]);

    const handleToggle = useCallback(
        (open: boolean) => {
            if (!open) {
                setWatchersPopup((state) => ({ ...state, usersWithoutAccess: [] }));
                onClose?.();
            }
        },
        [onClose, setWatchersPopup]
    );

    const postComment = useCallback(async () => {
        if (!request) {
            return;
        }

        if (checkedUserIds.length) {
            const watchersToAdd = checkedUserIds.reduce<Guid[]>((acc, userId) => {
                const user = users.find((user) => user.id === userId);

                if (user?.databaseId) {
                    acc.push(user.databaseId);
                }

                return acc;
            }, []);

            await changeWatchers(
                {
                    body: {
                        requestId: request.id,
                        companyId: request.companyId,
                        watchersToAdd,
                        watchersToRemove: [],
                        isAddedDueToMention: true,
                    },
                },
                {
                    onSuccess: () => {
                        onSubmit?.();
                        handleToggle(false);
                    },
                }
            );
        } else {
            onSubmit?.();
            handleToggle(false);
        }
    }, [request, checkedUserIds, changeWatchers, users, onSubmit, handleToggle]);

    return (
        <Popup size='small' open={!!users.length} preventAutoClose onToggle={handleToggle} zIndex={1000}>
            <Popup.Header title={messages.title} />

            <Popup.Body data-qa={qa()}>
                <Content>
                    <Description>{messages.description({ number: users.length })}</Description>

                    <ListStyled>
                        <PaddedGridHeader alignItems='center' width='100%'>
                            <div onClick={selectAllUsers}>
                                <Checkbox
                                    checked={checkedUserIds.length === users.length}
                                    triState={checkedUserIds.length > 0 && checkedUserIds.length < users.length}
                                />
                            </div>

                            <ListHeader>{messages.listHeader}</ListHeader>
                        </PaddedGridHeader>

                        <Divider />

                        <UserList users={users} checkedUserIds={checkedUserIds} setCheckedUserIds={setCheckedUserIds} />
                    </ListStyled>

                    <Button execute={postComment} disabled={isLoadingChangeWatchers}>
                        {checkedUserIds.length ? messages.postAndAdd({ number: checkedUserIds.length }) : messages.post}
                    </Button>
                </Content>
            </Popup.Body>
        </Popup>
    );
});

AddAsWatchersPopup.displayName = 'AddAsWatchersPopup';
